import type { SafetyStockEngineConfig } from '@/services/safetyStockService';
import { ABC_XYZ_KEYS } from '@/services/safetyStockService';

export type ConfigFieldErrors = Record<string, string>;

function toNumber(value: unknown): number | null {
  if (value === '' || value == null) return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function validateServiceLevel(value: unknown, field: string, errors: ConfigFieldErrors) {
  const n = toNumber(value);
  if (n == null) {
    errors[field] = 'required';
    return;
  }
  if (n <= 0 || n > 1) {
    errors[field] = 'serviceLevelRange';
  }
}

/** Validación espejo del serializer Django (parcial, para feedback inmediato). */
export function validateSafetyStockConfig(config: SafetyStockEngineConfig): ConfigFieldErrors {
  const errors: ConfigFieldErrors = {};

  validateServiceLevel(config.default_service_level, 'default_service_level', errors);

  const configured = toNumber(config.configured_safety_days);
  const maximum = toNumber(config.maximum_safety_days);
  if (configured == null || configured < 0) errors.configured_safety_days = 'gteZero';
  if (maximum == null || maximum < 0) errors.maximum_safety_days = 'gteZero';
  if (configured != null && maximum != null && maximum < configured) {
    errors.maximum_safety_days = 'maxGteConfigured';
  }

  const adi = toNumber(config.adi_threshold);
  const cv = toNumber(config.cv_squared_threshold);
  if (adi == null || adi < 0) errors.adi_threshold = 'gteZero';
  if (cv == null || cv < 0) errors.cv_squared_threshold = 'gteZero';

  if (!config.number_of_simulations || config.number_of_simulations < 1) {
    errors.number_of_simulations = 'gteOne';
  }

  const levels = config.service_levels || {};
  for (const key of ABC_XYZ_KEYS) {
    if (levels[key] == null) continue;
    validateServiceLevel(levels[key], `service_levels.${key}`, errors);
  }

  return errors;
}

export function cloneConfig(config: SafetyStockEngineConfig): SafetyStockEngineConfig {
  return structuredClone(config);
}

export function configsEqual(a: SafetyStockEngineConfig, b: SafetyStockEngineConfig): boolean {
  const pick = (c: SafetyStockEngineConfig) => ({
    time_unit: c.time_unit,
    configured_safety_days: Number(c.configured_safety_days),
    maximum_safety_days: Number(c.maximum_safety_days),
    minimum_lead_time_observations: c.minimum_lead_time_observations,
    minimum_positive_observations: c.minimum_positive_observations,
    number_of_simulations: c.number_of_simulations,
    random_seed: c.random_seed,
    adi_threshold: Number(c.adi_threshold),
    cv_squared_threshold: Number(c.cv_squared_threshold),
    service_measure: c.service_measure,
    default_service_level: Number(c.default_service_level),
    service_levels: c.service_levels,
    outliers: c.outliers,
    rounding: c.rounding,
    fallback: c.fallback,
    active_products_only: c.active_products_only,
  });
  return JSON.stringify(pick(a)) === JSON.stringify(pick(b));
}

/** Fracción (0.99) → porcentaje editable (99). */
export function fractionToPercentInput(value: string | number | null | undefined): string {
  const n = toNumber(value);
  if (n == null) return '';
  return String(Math.round(n * 10000) / 100);
}

/** Porcentaje editable (99) → fracción (0.99). */
export function percentInputToFraction(raw: string): number | null {
  const n = toNumber(raw.replace(',', '.'));
  if (n == null) return null;
  return n / 100;
}
